// Quick-open results: what can be reached by typing, how a query ranks it, and how picks are remembered.
// Pure so the ranking and grouping can be tested without the DOM; QuickOpen.tsx owns fetching and keys. (Named apart from QuickOpen.tsx: the two names would differ only in case.)
import type { MeOrganization } from "./auth";
import type { LoopModel } from "./pages/LoopsPage";
import { sharedByLine } from "./pages/sharingBulk";

export type QuickOpenKind = "loop" | "workspace" | "page";

export interface QuickOpenItem {
  kind: QuickOpenKind;
  /** Unique across kinds: "loop:<id>", "workspace:<id>", "page:<path>". Recent picks are stored by this id. */
  id: string;
  name: string;
  /** The one fact beside the name. Empty for pages. */
  fact: string;
  /** Secondary match terms (tags, source name, cadence, page aliases); they rank below any name match. */
  keywords: string[];
  /** Loops: the provider whose mark the row wears. */
  provider?: string | null;
  /** Loops: crossed a workspace boundary; the fact is the origin. */
  shared?: boolean;
  /** Loops and pages: where Enter goes. */
  href?: string;
  /** Workspaces: the organization to activate. */
  workspaceId?: string;
  /** Workspaces: the initial shown in place of a provider mark. */
  glyph?: string;
}

export interface QuickOpenGroup {
  label: "Recent" | "Pages" | "Workspaces" | "Loops";
  items: QuickOpenItem[];
}

export const RECENT_KEY = "lg.quickOpen.recent";
export const RECENT_LIMIT = 5;
/** Enough rows to answer a short query without the surface growing past the viewport. */
export const LOOP_LIMIT = 12;

/** 0 = the name starts with the query, 1 = the name contains it, 2 = only a keyword matches. */
export type MatchRank = 0 | 1 | 2;

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/gu, " ");
}

export function rankMatch(item: Pick<QuickOpenItem, "name" | "keywords">, query: string): MatchRank | null {
  const needle = normalize(query);
  if (!needle) return null;
  const name = normalize(item.name);
  if (name.startsWith(needle)) return 0;
  if (name.includes(needle)) return 1;
  if (item.keywords.some((keyword) => normalize(keyword).includes(needle))) return 2;
  return null;
}

const KIND_ORDER: Array<{ kind: QuickOpenKind; label: QuickOpenGroup["label"]; limit: number }> = [
  { kind: "loop", label: "Loops", limit: LOOP_LIMIT },
  { kind: "workspace", label: "Workspaces", limit: Number.POSITIVE_INFINITY },
  { kind: "page", label: "Pages", limit: Number.POSITIVE_INFINITY },
];

/**
 * An empty query shows the recent picks that still resolve, then the pages. A query shows each kind that
 * matches, loops first, ranked prefix > substring > keyword and then by name.
 */
export function groupResults(items: QuickOpenItem[], query: string, recent: string[]): QuickOpenGroup[] {
  if (!normalize(query)) {
    const byId = new Map(items.map((item) => [item.id, item]));
    const recentItems = recent.map((id) => byId.get(id)).filter((item): item is QuickOpenItem => Boolean(item)).slice(0, RECENT_LIMIT);
    const pages = items.filter((item) => item.kind === "page");
    const groups: QuickOpenGroup[] = [];
    if (recentItems.length) groups.push({ label: "Recent", items: recentItems });
    if (pages.length) groups.push({ label: "Pages", items: pages });
    return groups;
  }
  const groups: QuickOpenGroup[] = [];
  for (const { kind, label, limit } of KIND_ORDER) {
    const ranked = items
      .filter((item) => item.kind === kind)
      .map((item) => ({ item, rank: rankMatch(item, query) }))
      .filter((entry): entry is { item: QuickOpenItem; rank: MatchRank } => entry.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.item.name.localeCompare(b.item.name, "en", { sensitivity: "base" }))
      .slice(0, limit)
      .map((entry) => entry.item);
    if (ranked.length) groups.push({ label, items: ranked });
  }
  return groups;
}

/** Reliability with its sample once evaluable; the gate while withheld; the origin when the loop is shared. */
export function loopFact(loop: LoopModel): string {
  if (loop.visibility.mode === "shared") return sharedByLine({ ownerName: loop.owner?.name, originName: loop.visibility.originOrganizationName, rule: null });
  const { runs, resolvedRuns, successRate } = loop.performance;
  if (runs === 0) return "no runs yet";
  if (resolvedRuns >= 3 && successRate != null) return `${Math.round(successRate * 100)}% · ${resolvedRuns} resolved`;
  return "— · needs 3 resolved";
}

export function loopItem(loop: LoopModel): QuickOpenItem {
  const keywords = [...loop.tags, loop.discovery?.source.name ?? "", loop.cadence].filter(Boolean);
  return {
    kind: "loop",
    id: `loop:${loop.id}`,
    name: loop.name,
    fact: loopFact(loop),
    keywords,
    provider: loop.runsOn?.provider ?? loop.discovery?.source.provider ?? null,
    shared: loop.visibility.mode === "shared",
    href: `/app/loops/${loop.id}`,
  };
}

export function workspaceItem(organization: MeOrganization, memberCount?: number | null): QuickOpenItem {
  const fact = organization.kind === "personal" ? "Private" : memberCount == null ? "Group" : `Group · ${memberCount} ${memberCount === 1 ? "member" : "members"}`;
  return {
    kind: "workspace",
    id: `workspace:${organization.id}`,
    name: organization.name,
    fact,
    keywords: organization.kind === "personal" ? ["private", "workspace"] : ["group", "workspace"],
    workspaceId: organization.id,
    glyph: organization.name.trim().slice(0, 1).toUpperCase() || "·",
  };
}

/** The five pages of the sidebar, with the people page named the way the sidebar names it. */
export function pageItems(activeKind: MeOrganization["kind"] | undefined): QuickOpenItem[] {
  const pages: Array<{ href: string; name: string; keywords: string[] }> = [
    { href: "/app", name: "Pulse", keywords: ["overview", "home", "dashboard"] },
    { href: "/app/loops", name: "Loops", keywords: ["registry", "catalog"] },
    { href: "/app/onboarding", name: "Sources", keywords: ["scan", "agents", "connect", "onboarding"] },
    { href: "/app/people", name: activeKind === "group" ? "Group" : "Sharing", keywords: ["people", "members", "invitations", "sharing", "group", "teams"] },
    { href: "/app/settings", name: "Settings", keywords: ["keys", "agent keys", "cards", "account"] },
  ];
  return pages.map((page) => ({ kind: "page", id: `page:${page.href}`, name: page.name, fact: "", keywords: page.keywords, href: page.href }));
}

export function readRecent(storage: Pick<Storage, "getItem">): string[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

export function rememberRecent(recent: string[], id: string): string[] {
  return [id, ...recent.filter((entry) => entry !== id)].slice(0, RECENT_LIMIT);
}

export function shortcutLabel(platform: string): "⌘K" | "Ctrl+K" {
  return /mac|iphone|ipad|ipod/iu.test(platform) ? "⌘K" : "Ctrl+K";
}
